import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import useEntryMutation from '~/domains/contest/domains/services/hooks/useEntryMutation';
import { isSplashApiError } from '~/domains/services/utils/isBEApiError';

import EditEntryNameModal from '../../components/EntryNameEditModal/EntryNameEditModal';

type EntryInfo = {
  id: string;
  name: string;
};
type EntryNameEditModalContextValue = {
  openEntryNameEditModal: (entry: EntryInfo) => void;
};

const EntryNameEditModalContext = createContext<EntryNameEditModalContextValue>({
  openEntryNameEditModal: () => {},
});

export function EntryNameEditModalContextProvider({ children }: PropsWithChildren) {
  const [entry, setEntry] = useState<EntryInfo>();
  const { t } = useTranslation('common');

  const mutation = useEntryMutation(entry?.id);

  const handleClose = useCallback(() => {
    setEntry(undefined);
  }, []);

  const value = useMemo(
    () => ({
      openEntryNameEditModal: setEntry,
    }),
    []
  );

  const apiError = useMemo(() => {
    if (!mutation.error) {
      return null;
    }

    if (isSplashApiError(mutation.error)) {
      return mutation.error.response?.data?.message;
    }

    return (mutation.error as Error).message ?? t('errors.unknown.title');
  }, [mutation.error, t]);

  return (
    <EntryNameEditModalContext.Provider value={value}>
      {children}
      <EditEntryNameModal
        isOpened={!!entry}
        defaultEntryName={entry?.name ?? ''}
        onClose={handleClose}
        onSubmit={mutation.mutateAsync}
        isLoading={mutation.isLoading}
        error={apiError}
      />
    </EntryNameEditModalContext.Provider>
  );
}

export default EntryNameEditModalContext;
